import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { IonPage, IonItem, IonCheckbox } from "@ionic/react";
import {
  Button,
  Card,
  Typography,
  Divider,
  Row,
  Col,
  Form,
  Menu,
  Radio,
  Avatar,
  Dropdown,
  Modal,
  Input,
  Select,
  DatePicker,
  Checkbox,
  Space,
  Drawer,
  Switch,
  notification,
} from "antd";
import { PlusOutlined, DeleteFilled } from "@ant-design/icons";

//
import log from "../utils/log";
import c from "../utils/common";
import dayjs from "dayjs";
import metaData from "../data/metaData";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import useCompanyStore from "../states/useCompany";
import produce from "immer";

//
import SectionHeader from "./GetStarted/SectionHeader";
import Name from "./General/Name";
import Description from "./General/Description";
import Gallery from "./General/Gallery";
import deepcopy from "deepcopy";
import Price from "./General/Price";
import Number from "./General/Number";
import ItemSelectionDrawer from "./ItemSelectionDrawer";
import PeriodPicker from "./General/PeriodPicker";
import CheckBox from "./General/Checkbox";
import PriceDiscountPreview from "./Product/PriceDiscountPreview";
import QtyDiscountPreview from "./Product/QtyDiscountPreview";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const cardStyle = {
  border: "2px solid var(--ion-color-step-150)", // Change the color and width as needed
};

const TermsPicker = ({ setIsShowCustomTermModal, setIsShowTermModal }) => {
  const { t, i18n } = useTranslation();

  return (
    <Row align="middle">
      <Title level={5} style={{ marginRight: "1rem" }}>
        Terms
      </Title>
      <Dropdown
        overlay={
          <Menu
            items={[
              {
                label: <strong>Custom Term</strong>,
                key: "1",
                onClick: () => {
                  setIsShowCustomTermModal(true);
                },
              },
              {
                label: <strong>{t("ticket.Template")}</strong>,
                key: "2",
                onClick: () => {
                  setIsShowTermModal(true);
                },
              },
            ]}
          />
        }
        trigger={["click"]}
      >
        <Button type="primary" shape="circle" icon={<PlusOutlined />} />
      </Dropdown>
    </Row>
  );
};

const TermsDisplay = ({ state = {} }) => {
  const { data, form, onChange } = state;

  return data?.length > 0
    ? data.map((term, i) => (
        <div key={i} style={{ width: "100%", margin: "1rem 0" }}>
          <Row gutter={8} justify="space-between">
            <Col span={3}>
              <Avatar style={{ background: "var(--ion-color-primary-200)", color: "black" }}>{i + 1}</Avatar>
            </Col>
            <Col span={19} style={{ fontSize: "15px" }}>
              {term}
            </Col>
            <Col
              span={2}
              onClick={() => {
                let updated = deepcopy(data);
                updated = updated.filter((e) => e != term);
                onChange({
                  terms: updated,
                });
              }}
            >
              <DeleteFilled style={{ cursor: "pointer", fontSize: "15px" }} />
            </Col>
          </Row>
          {i == data.length - 1 ? null : <Divider style={{ margin: 6 }} />}
        </div>
      ))
    : null;
};

const CustomTermModal = ({ isShow, onClose, onConfirm }) => {
  const { t, i18n } = useTranslation();
  const [term, setTerm] = useState();

  return (
    <Modal
      closable
      centered
      title={t("ticket.AddTerms")}
      open={isShow}
      onOk={() => {
        onConfirm();
      }}
      onCancel={() => {
        onClose();
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onClose();
          }}
        >
          {t("ticket.Cancel")}
        </Button>,
        <Button
          key="add"
          onClick={() => {
            let value = deepcopy(term);
            value = value || undefined;
            setTerm();
            onConfirm(value);
          }}
          type="primary"
        >
          {t("ticket.Complete")}
        </Button>,
      ]}
    >
      <TextArea rows={4} value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Enter your terms here" />
    </Modal>
  );
};

const TermModal = ({ isShow, onClose, onConfirm }) => {
  const { t, i18n } = useTranslation();
  let defaultOptions = [
    "Shipping fee not included",
    "Not applicable to use in conjunction with other offers or discounts",
    "Not applicable for simultaneous use with Happy Hour and other special offers",
    "Not valid during the sales period or during any special promotions",
    "Only valid for a single transaction",
    "Can only be redeemed on working days",
    "This giftcard / voucher / discount is non-refundable and cannot be exchanged for partial or full cash",
    "In case of any dispute, the company reserves the right of final decision",
    "The terms and conditions of this giftcard / voucher / discount are binding on the customer",
    "There will be no refund or compensation for unused vouchers / discount after the expiry date",
    // Gift Card terms
    "Treat this e-Gift Card like cash, as it holds stored value",
    "Present this e-Gift Card as payment towards your purchases and the business will deduct the purchase amount from the available balance on this e-Gift Card",
    "You may continue to use this e-Gift Card until the balance reaches zero",
    "This e-Gift Card is non-refundable and has no cash value, except as required by law",
    "This e-Gift Card cannot be redeemed or exchanged for cash or used to purchase other Gift Cards or e-Gift Cards",
    "Resale of this e-Gift Card for commercial purposes or use for unauthorized advertising, marketing or sweepstakes purposes is strictly prohibited",
    "This e-Gift Card will not be replaced if lost, stolen or destroyed",
    "The current balance of this e-Gift Card can be viewed on the Pearl Mobile App",
    "Use of this e-Gift Card constitutes acceptance of these terms and conditions",
  ].map((e) => {
    return { value: e, isChecked: false };
  });
  const [termOptions, setTermOptions] = useState(defaultOptions);

  return (
    <Modal
      closable
      centered
      title={t("ticket.AddTerms")}
      open={isShow}
      onOk={() => {
        onConfirm();
      }}
      onCancel={() => {
        onClose();
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onClose();
          }}
        >
          {t("ticket.Cancel")}
        </Button>,
        <Button
          key="add"
          onClick={() => {
            let value = deepcopy(termOptions);
            setTermOptions(defaultOptions);
            onConfirm(value);
          }}
          type="primary"
        >
          {t("ticket.Complete")}
        </Button>,
      ]}
    >
      <div style={{}}>
        {termOptions?.length > 0
          ? termOptions.map((e, i) => (
              <IonItem style={{ paddingBottom: "5px" }} key={i}>
                <span style={{ fontSize: "15px", paddingBottom: "5px" }}>{e.value}</span>
                <IonCheckbox
                  slot="start"
                  value={e.value}
                  checked={e.isChecked}
                  onIonChange={(e) => {
                    termOptions[i].isChecked = e.detail.checked;
                  }}
                />
              </IonItem>
            ))
          : null}
      </div>
    </Modal>
  );
};

const ProductTypePicker = ({ state = {} }) => {
  const { t, i18n } = useTranslation();
  const { data, form, onChange } = state;

  // Use setFieldsValue to update the form values dynamically
  useEffect(() => {
    if (data?.type) {
      form.setFieldsValue({
        type: data.type,
      });
    }
    form.setFieldsValue({
      "discount.type": data?.discount?.type,
    });
  }, [data, form]);

  return (
    <>
      <Row>
        <Title level={5}>Type</Title>
        <Form form={form} style={{ width: "100%" }}>
          <Form.Item name="type" rules={[{ required: true, message: "required" }]}>
            <Select
              placeholder="please select..."
              size="large"
              value={data?.type}
              onChange={(e) => {
                onChange({
                  price: 0,
                  isHidePrice: false,
                  isReferral: false,
                  type: e,
                  discount: undefined,
                  discountMethod: undefined,
                  qty: { total: 0 },
                });
              }}
            >
              <Option value="voucher">{t("ticket.Voucher")}</Option>
              <Option value="discount">{t("ticket.Discount")}</Option>
              <Option value="giftCard">Gift Card</Option>
              {/* <Option value="takeaway">{t("ticket.TakeAway")}</Option> */}
            </Select>
          </Form.Item>
        </Form>
      </Row>

      {data?.type == "discount" ? (
        <Row>
          <Title level={5} style={{ marginRight: "2rem" }}>
            Discount Type
          </Title>
          <Form form={form} style={{ width: "100%" }}>
            <Form.Item name="discount.type" rules={[{ required: true, message: "required" }]}>
              <Radio.Group
                style={{ marginBottom: 24 }}
                value={data?.discount?.type}
                size="large"
                buttonStyle="solid"
                onChange={(e) => {
                  onChange({
                    price: 0,
                    type: "discount",
                    discount: {
                      type: e.target.value,
                    },
                    discountMethod: undefined,
                    qty: { total: 0 },
                  });
                }}
              >
                <Radio.Button value="price">Price</Radio.Button>
                <Radio.Button value="percentage">Percentage</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Form>
        </Row>
      ) : null}
    </>
  );
};

const DiscountMethod = ({ state = {}, setIsShowDiscountMethodDrawer }) => {
  const { data, form, onChange } = state;

  return (
    <Row style={{ marginBottom: "2rem" }}>
      <Title level={5} style={{ marginBottom: "1rem" }}>
        Discount Method <Text type="secondary">(optional)</Text>
      </Title>
      <Card style={{ width: "25vw" }}>
        {data?.discountMethod ? (
          <>
            <Row>
              {data?.discountMethod?.trigger?.type == "qty" ? (
                <QtyDiscountPreview
                  buy={data?.discountMethod?.trigger?.value}
                  free={data?.discountMethod?.reward?.value}
                />
              ) : data?.discountMethod?.trigger?.type == "none" ? (
                <PriceDiscountPreview price={data?.price} sellingPrice={data?.discountMethod?.reward?.value} />
              ) : null}
            </Row>
            <Row>
              <Button
                size="large"
                type="primary"
                style={{ marginTop: "1rem" }}
                onClick={() => {
                  setIsShowDiscountMethodDrawer(true);
                }}
              >
                Update
              </Button>
            </Row>
          </>
        ) : (
          <>
            <Row style={{ width: "100%" }}>
              <Text type="secondary">No discount applied</Text>
            </Row>
            <Button
              size="large"
              type="primary"
              style={{ marginTop: "1rem" }}
              onClick={() => {
                setIsShowDiscountMethodDrawer(true);
              }}
            >
              Choose one
            </Button>
          </>
        )}
      </Card>
    </Row>
  );
};

const DiscountMethodDrawer = ({ state = {}, price, isShow, header, onClose, onConfirm }) => {
  const { data, form, onChange } = state;
  const [discountMethod, setDiscountMethod] = useState();

  const isSaveOptionDisabled = price <= 1;

  const handleCloseDrawer = () => {
    setDiscountMethod(data);
    onClose();
  };

  const isFormValid = () => {
    if (!discountMethod) {
      notification.error({ message: "Please select a discount method" });
      return false;
    }
    if (!discountMethod?.reward?.value) {
      notification.error({ message: "Please complete the form" });
      return false;
    }
    if (discountMethod?.trigger?.type === "qty" && !discountMethod?.trigger?.value) {
      notification.error({ message: "Please complete the form" });
      return false;
    }
    return true;
  };

  useEffect(() => {
    setDiscountMethod(data);
  }, [data]);

  return (
    <Drawer
      open={isShow}
      title={header}
      width="40%"
      onClose={handleCloseDrawer}
      closable={true}
      extra={
        <Button
          type="primary"
          size="large"
          disabled={false}
          onClick={() => {
            if (isFormValid()) {
              onConfirm(discountMethod);
            }
          }}
        >
          Confirm
        </Button>
      }
    >
      <Title level={5}>Select One Method:</Title>
      {/* <div>{JSON.stringify(discountMethod)}</div> */}

      <Row
        align="middle"
        style={{ width: "100%", margin: "1rem 0", cursor: "pointer" }}
        onClick={() => {
          setDiscountMethod(
            produce((draft = {}) => {
              // Ensure draft is initialized as an empty object if undefined
              draft.trigger = { type: "qty" }; // Replace trigger object entirely
              draft.reward = { type: "qty" };
              return draft;
            })
          );
        }}
      >
        <Col style={{ width: "2rem" }}>
          <CheckBox checked={discountMethod?.trigger?.type === "qty"} />
        </Col>
        <Col style={{ marginBottom: "-0.5rem" }}>
          <Text strong style={{ fontSize: "1.25rem" }}>
            Buy X Get Y Free
          </Text>
        </Col>
      </Row>

      <Row
        align="middle"
        style={{ width: "100%", margin: "1rem 0 0 0", cursor: isSaveOptionDisabled ? "not-allowed" : "pointer" }}
        onClick={() => {
          if (!isSaveOptionDisabled) {
            setDiscountMethod(
              produce((draft = {}) => {
                // Ensure draft is initialized as an empty object if undefined
                draft.trigger = { type: "none" }; // Replace trigger object entirely
                draft.reward = { type: "sellingPrice" };
                return draft;
              })
            );
          }
        }}
      >
        <Col style={{ width: "2rem" }}>
          <CheckBox checked={discountMethod?.trigger?.type === "none"} disabled={isSaveOptionDisabled} />
        </Col>
        <Col style={{ marginBottom: "-0.5rem" }}>
          <Text strong style={{ fontSize: "1.25rem" }}>
            Discounted Selling Price
          </Text>
        </Col>
      </Row>
      {isSaveOptionDisabled ? (
        <Text strong type="danger" style={{ marginLeft: "2rem" }}>
          Original price is too low, cannot not apply this discount
        </Text>
      ) : null}

      <Divider />

      {discountMethod?.trigger?.type === "qty" && (
        <>
          <Row style={{ marginBottom: "2rem" }}>
            <Row style={{ width: "100%" }}>
              <Title level={5}>How many items need to be purchased to qualify for the discount?</Title>
            </Row>
            <Number
              style={{ width: "10rem", inputFontSize: "18px" }}
              suffix="Qty"
              state={{
                data: discountMethod?.trigger?.value,
                form: form,
                rules: [
                  { required: true, message: "required" },
                  {
                    validator: (_, value) =>
                      value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                  },
                ],
                onChange: (value) =>
                  value > 0 &&
                  setDiscountMethod(
                    produce((draft = {}) => {
                      // Ensure draft is initialized as an empty object if undefined
                      draft.trigger.value = value; // Replace trigger object entirely
                      return draft;
                    })
                  ),
              }}
              min={1}
              keyName="trigger.value"
              readOnly={false}
            />
          </Row>

          <Row style={{ marginBottom: "2rem" }}>
            <Row style={{ width: "100%" }}>
              <Title level={5}>How many items will the customer get for free?</Title>
            </Row>
            <Number
              style={{ width: "10rem", inputFontSize: "18px" }}
              suffix="Qty"
              disabled={!discountMethod?.trigger?.value}
              max={discountMethod?.trigger?.value - 1 || 0}
              state={{
                data: discountMethod?.reward?.value,
                form: form,
                rules: [
                  { required: true, message: "required" },
                  {
                    validator: (_, value) =>
                      value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                  },
                ],
                onChange: (value) =>
                  value > 0 &&
                  setDiscountMethod(
                    produce((draft = {}) => {
                      // Ensure draft is initialized as an empty object if undefined
                      draft.reward = draft?.reward || {};
                      draft.reward.value = value; // Replace trigger object entirely
                      return draft;
                    })
                  ),
              }}
              min={1}
              keyName="reward.value"
              readOnly={false}
            />
            <QtyDiscountPreview buy={discountMethod?.trigger?.value} free={discountMethod?.reward?.value} />
          </Row>
        </>
      )}

      {discountMethod?.trigger?.type === "none" && (
        <Row style={{ marginBottom: "2rem" }}>
          <Row style={{ width: "100%" }}>
            <Title level={5}>Discounted selling price</Title>
          </Row>
          <Number
            style={{ width: "10rem", inputFontSize: "18px" }}
            prefix="$"
            max={Math.max(price - 1, 1)} // Fix: Prevents errors for low price
            state={{
              data: discountMethod?.reward?.value,
              form: form,
              rules: [
                { required: true, message: "required" },
                {
                  validator: (_, value) =>
                    value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                },
              ],
              onChange: (value) =>
                value > 0 &&
                setDiscountMethod(
                  produce((draft = {}) => {
                    // Ensure draft is initialized as an empty object if undefined
                    draft.reward = draft?.reward || {};
                    draft.reward.value = value; // Replace trigger object entirely
                    return draft;
                  })
                ),
            }}
            min={1}
            keyName="reward.value"
            readOnly={false}
          />
          <PriceDiscountPreview price={price} sellingPrice={discountMethod?.reward?.value} />
        </Row>
      )}
    </Drawer>
  );
};

const ProductProfileEdit = ({ state = {}, allianceShops = [] }) => {
  const { t, i18n } = useTranslation();
  const { data, form, onChange } = state;
  const { company, getMaxShopExpiry } = useCompanyStore();

  const [isShowTermModal, setIsShowTermModal] = useState(false);
  const [isShowCustomTermModal, setIsShowCustomTermModal] = useState(false);
  const [isShowRedeemShopDrawer, setIsShowRedeemShopDrawer] = useState(false);
  const [isShowDiscountMethodDrawer, setIsShowDiscountMethodDrawer] = useState(false);

  log.debug(data);

  // Handler to wrap data into the 'product' object
  const updateProductData = (data) => {
    onChange({
      product: {
        ...data,
      },
    });
  };

  // Handler to wrap data into the 'product' object
  const handleTermsChange = (options) => {
    // log.debug(options);
    let updated;

    let existing = data?.terms?.length > 0 ? data.terms : [];
    // log.debug(existing);

    if (typeof options == "string") {
      updated = [...new Set([...existing, options])];
    } else {
      updated = [...new Set([...existing, ...options.filter((e) => e.isChecked).map((e) => e.value)])];
    }

    onChange({
      product: {
        terms: updated,
      },
    });
  };

  return (
    <Card
      // title={<SectionHeader title="Product" />}
      style={{
        ...cardStyle,
        position: "relative", // Enable positioning for the overlay
        overflow: "hidden", // Ensure the overlay doesn't exceed card boundaries
      }}
    >
      <TermModal
        isShow={isShowTermModal}
        onClose={() => {
          setIsShowTermModal(false);
        }}
        onConfirm={(value) => {
          handleTermsChange(value);
          setIsShowTermModal(false);
        }}
      />

      <CustomTermModal
        isShow={isShowCustomTermModal}
        onClose={() => {
          setIsShowCustomTermModal(false);
        }}
        onConfirm={(value) => {
          handleTermsChange(value);
          setIsShowCustomTermModal(false);
        }}
      />

      <ItemSelectionDrawer
        state={{
          data: data?.redeemShopIds,
          form: form,
          onChange: (shopIds, shops) => {
            updateProductData({
              redeemShopIds: shopIds,
              redeemShops: shops,
            });
          },
        }}
        header={
          <SectionHeader
            title="Select Redeem Shops"
            description={"select shops that can redeem this product"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        items={allianceShops}
        isShow={isShowRedeemShopDrawer}
        tableType="shop"
        confirmBtnText={"OK"}
        onConfirm={() => {
          setIsShowRedeemShopDrawer(false);
        }}
        onClose={() => {
          setIsShowRedeemShopDrawer(false);
        }}
      />

      <DiscountMethodDrawer
        state={{
          data: data?.discountMethod,
          form: form,
          onChange: () => {},
        }}
        price={data?.price}
        header={
          <SectionHeader
            title="Discount Method"
            // description={"select shops that can redeem this product"}
            style={{ margin: "0 2rem" }}
          />
        }
        isShow={isShowDiscountMethodDrawer}
        onConfirm={(discountMethod) => {
          updateProductData({
            discountMethod: discountMethod,
          });
          setIsShowDiscountMethodDrawer(false);
        }}
        onClose={() => {
          setIsShowDiscountMethodDrawer(false);
        }}
      />

      <Row>
        <Row style={{ width: "100%" }}>
          <Title level={5}>Cover Photo</Title>
        </Row>
        <Row style={{ marginBottom: "2rem", width: "100%" }}>
          <Text type="secondary" style={{}}>
            Max 1 photo, 1:1, JPG or PNG. Max size of 5MB
          </Text>
        </Row>

        <Gallery
          state={{
            data: data?.gallery,
            onChange: updateProductData,
          }}
          aspect={1}
          readOnly={false}
          width={"15rem"}
          MAX={1}
        />

        <Divider style={{ background: "var(--ion-color-step-200)", height: "2px", margin: "3rem 0" }} />

        <Row justify="space-between" style={{ width: "100%" }}>
          <Col span={8}>
            <ProductTypePicker
              state={{
                data: data,
                form: form,
                onChange: updateProductData,
              }}
            />

            {data?.type === "voucher" ? (
              <Row style={{ marginBottom: "2rem" }}>
                <Row style={{ width: "100%" }}>
                  <SectionHeader
                    title={
                      <Title level={5} style={{ margin: "1rem 0 0 0" }}>
                        For referral only?
                      </Title>
                    }
                    description='Selecting "Yes" means this voucher can only be used in referral campaigns.'
                  />
                  {/* <Title level={5}>For referral only?</Title>
                  <Text type="secondary" style={{ marginBottom: "1rem" }}>
                    Selecting "Yes" means this voucher can only be used in referral campaigns.
                  </Text> */}
                </Row>

                <Radio.Group
                  size="large"
                  optionType="button"
                  buttonStyle="solid"
                  value={data?.isReferral}
                  onChange={(e) => {
                    updateProductData({
                      isReferral: e.target.value,
                    });
                  }}
                >
                  <Radio.Button value={true}>Yes</Radio.Button>
                  <Radio.Button value={false}>No</Radio.Button>
                </Radio.Group>
              </Row>
            ) : null}

            {data?.type == "voucher" ||
            data?.type == "giftCard" ||
            (data?.type == "discount" && data?.discount?.type == "price") ? (
              <Row align="middle" style={{ marginBottom: "2rem" }}>
                <Title level={5}>
                  {data.type == "voucher"
                    ? "Value"
                    : data.type == "discount"
                    ? "Save"
                    : data.type == "giftCard"
                    ? "Balance"
                    : null}
                </Title>
                <Price
                  state={{
                    data: data?.price,
                    form,
                    rules: [
                      { required: true, message: "required" },
                      {
                        validator: (_, value) => {
                          if (data?.type === "voucher" && !data?.isHidePrice && value <= 0) {
                            return Promise.reject(new Error("Must be greater than zero"));
                          }
                          return Promise.resolve();
                        },
                      },
                    ],
                    onChange: (value) => {
                      if (value > 0) {
                        updateProductData({ price: value });
                      }
                    },
                  }}
                  min={1}
                  readOnly={false}
                  disabled={data?.isHidePrice}
                  style={{ inputFontSize: "18px" }}
                />

                {data?.type == "voucher" || data?.type == "discount" ? (
                  <>
                    <Switch
                      checked={data?.isHidePrice}
                      onChange={(checked) =>
                        updateProductData({
                          isHidePrice: checked,
                          price: checked ? 0 : data.price,
                          discountMethod: undefined, // Reset discountMethod
                        })
                      }
                    />
                    <Text style={{ marginLeft: "1rem" }}>Do not display</Text>
                  </>
                ) : null}
              </Row>
            ) : null}

            {(data?.type === "voucher" || data?.type === "giftCard") && !data?.isReferral && data?.price > 0 ? (
              <DiscountMethod
                state={{
                  data: data,
                  form: form,
                  onChange: updateProductData,
                }}
                setIsShowDiscountMethodDrawer={setIsShowDiscountMethodDrawer}
              />
            ) : null}

            {data?.type == "discount" && data?.discount?.type == "percentage" ? (
              <Row align="middle" style={{ marginBottom: "2rem" }}>
                <Row>
                  <Title level={5}>{t("ticket.Percentage")}</Title>
                </Row>
                <Number
                  state={{
                    data: data?.discount?.percentage,
                    form: form,
                    rules: [
                      { required: true, message: "required" },
                      {
                        validator: (_, value) =>
                          value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                      },
                    ],
                    onChange: (value) =>
                      value > 0 && updateProductData({ discount: { ...data?.discount, percentage: value } }),
                  }}
                  min={1}
                  max={100}
                  keyName="percentage"
                  readOnly={false}
                  style={{ inputFontSize: "18px" }}
                  addonAfter={"% off"}
                />
              </Row>
            ) : null}

            {data?.type == "voucher" || data?.type == "giftCard" ? (
              <Row style={{ marginBottom: "2rem" }}>
                <Title level={5}>Total Supply</Title>
                <Number
                  state={{
                    data: data?.qty?.total,
                    form: form,
                    rules: [
                      { required: true, message: "required" },
                      {
                        validator: (_, value) =>
                          value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                      },
                    ],
                    onChange: (value) => value > 0 && updateProductData({ qty: { total: value } }),
                  }}
                  min={1}
                  keyName="qty.total"
                  readOnly={false}
                  style={{ inputFontSize: "18px" }}
                />
              </Row>
            ) : null}
          </Col>
        </Row>
      </Row>

      <Divider style={{ background: "var(--ion-color-step-200)", height: "2px", margin: "3rem 0" }} />

      <Title level={5}>Product Name</Title>
      <Name
        state={{
          data: data?.name,
          form: form,
          rules: [{ required: true, message: "required" }],
          onChange: updateProductData,
        }}
        readOnly={false}
        maxLength={60}
        showCount
      />

      <Title level={5}>Description</Title>
      <Description
        state={{
          data: data?.description,
          form: form,
          onChange: updateProductData,
          keyName: "description",
        }}
        readOnly={false}
        placeholder={"A brief description of your product"}
      />

      <TermsPicker setIsShowCustomTermModal={setIsShowCustomTermModal} setIsShowTermModal={setIsShowTermModal} />

      <TermsDisplay
        state={{
          data: data?.terms,
          form: form,
          onChange: updateProductData,
        }}
      />

      <Divider style={{ background: "var(--ion-color-step-200)", height: "2px", margin: "3rem 0" }} />

      {data?.type == "discount" || data?.isHidePrice || data?.isReferral ? null : (
        <>
          <Title level={5}>Sale Period</Title>
          <PeriodPicker
            state={{
              data: data?.time?.sale,
              form: form,
              rules: [{ required: true, message: "required" }],
              onChange: (updated) => {
                updateProductData({
                  time: { sale: updated },
                });
              },
              keyName: "salePeriod",
            }}
            minDate={dayjs()} // Minimum date for the sale period is the current date
            maxDate={
              data?.time?.redeem
                ? (() => {
                    const redeemEnd = dayjs(data.time.redeem.end);
                    const maxShopExpiry = getMaxShopExpiry();
                    return redeemEnd.isBefore(maxShopExpiry) ? redeemEnd : maxShopExpiry;
                  })()
                : getMaxShopExpiry()
            }
          />
        </>
      )}

      {data?.type == "voucher" || data?.type == "discount" ? (
        <>
          <Title level={5}>Redeem Period</Title>
          <PeriodPicker
            state={{
              data: data?.time?.redeem,
              form: form,
              rules: [{ required: true, message: "required" }],
              onChange: (updated) => {
                updateProductData({
                  time: { redeem: updated },
                });
              },
              keyName: "redeemPeriod",
            }}
            minDate={dayjs()} // Minimum date for the redeem period is the current date
            maxDate={getMaxShopExpiry()} // Maximum date is the max shop expiry date
          />
        </>
      ) : null}

      <SectionHeader
        title={
          <Title level={5} style={{ margin: "1rem 0 0 0" }}>
            Redeem Shop List
          </Title>
        }
        description={"Select shop that can redeem this product, you can update this list later"}
      />

      <Card style={{ width: "25vw" }}>
        <Row justify="space-between" align="middle">
          <Title level={5}>Selected {data?.redeemShopIds?.length > 0 ? data?.redeemShopIds?.length : 0} Shops</Title>
          <Button
            size="large"
            type="primary"
            loading={!allianceShops?.length > 0}
            style={{ marginLeft: "2rem" }}
            onClick={() => {
              setIsShowRedeemShopDrawer(true);
            }}
          >
            Manage
          </Button>
        </Row>
      </Card>
    </Card>
  );
};

export default ProductProfileEdit;
